import React, { useMemo, useState } from 'react';
import EvidenceCard from '../../components/EvidenceCard';
import DomainAuditCard from '../../components/ui/DomainAuditCard';
import DeliverabilityCard from '../../components/DeliverabilityCard';
import GHuntIdentityCard from '../../components/GHuntIdentityCard';
import HibpCard from '../../components/HibpCard';
import EmailInfrastructureSection from '../../components/EmailInfrastructureSection';
import { useFeedInspect } from '../../components/FeedInspectProvider';
import WaybackTimelineCard from '../../components/WaybackTimelineCard';
import ServiceFavicon from '../../components/Email/ServiceFavicon';
import { subscriptionHrefForService } from '../../utils/investigationWorkspaceUtils';
import { REGISTERED_PLATFORMS_COLLAPSED_LIMIT } from '../../utils/serviceFavicon';
import {
  collectNativeProbeStatuses,
  filterProbeStatusesAgainstRegistered,
  PROBE_STATE_STYLES,
  probeStateLabel,
  probeStatusTitle,
} from '../../utils/nativeProbeStatus';
import {
  FEED_CARD_SHELL,
} from '../../components/investigation/feedCardStyles';
import CategoryTabs from '../../components/investigation/CategoryTabs';
import DarkWebAdvisoryCard from '../../components/DarkWebAdvisoryCard';
import {
  dedupeEmailInfrastructureRows,
  isPublicEmailProvider,
  mergeEmailServerRoutingRows,
} from '../../utils/emailIntelUi';
import { buildEmailPersonPivotContext } from '../../utils/emailPersonPivot';

/** @typedef {'all'|'identity'|'breaches_risk'|'infrastructure'|'platforms'|'mentions'} EmailFeedFilterId */

export const EMAIL_FEED_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'identity', label: 'Identity' },
  { id: 'breaches_risk', label: 'Breaches & Risk' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'mentions', label: 'Mentions' },
];

function showEmailSection(activeFilter, sectionId) {
  return activeFilter === 'all' || activeFilter === sectionId;
}

const PLATFORM_FEED_SOURCES = new Set([
  'holehe',
  'blackbird',
  'socialscan',
  'profile-hound',
  'profilehound',
  'serp_dorking',
]);

function SectionHeader({ title, subtitle, tone = 'slate', count }) {
  const tones = {
    emerald: 'text-emerald-300',
    red: 'text-red-300',
    blue: 'text-blue-300',
    cyan: 'text-cyan-300',
    amber: 'text-amber-200',
    slate: 'text-slate-300',
  };
  return (
    <div className="flex flex-wrap items-end justify-between gap-2 px-1">
      <div>
        <h4 className={`text-[11px] font-black uppercase tracking-[0.12em] sm:text-xs sm:tracking-[0.14em] ${tones[tone] || tones.slate}`}>
          {title}
        </h4>
        {subtitle ? <p className="mt-1 text-xs text-slate-300 sm:text-[13px]">{subtitle}</p> : null}
      </div>
      {typeof count === 'number' ? (
        <span className="rounded-lg border border-white/15 bg-slate-950/80 px-2.5 py-1 text-[11px] font-black uppercase tracking-wider text-slate-200 sm:text-xs">
          {count} active
        </span>
      ) : null}
    </div>
  );
}

function emailResultKey(res, idx) {
  if (res.ui_bucket === 'email_deliverability') return `email-feed-deliverability-${res.email || idx}`;
  if (res.ui_bucket === 'ghunt_footprint') return `email-feed-ghunt-${res.gaia_id || idx}`;
  if (res.ui_bucket === 'registered_service')
    return `email-feed-service-${res.service_key || res.service_name || res.domain || idx}`;
  if (res.ui_bucket === 'cyber_trust_score') return `email-feed-cyber-${res.domain || idx}`;
  if (res.ui_bucket === 'threat_intelligence') return `email-feed-vt-${res.domain || idx}`;
  if (res.ui_bucket === 'wayback_archive')
    return `email-feed-wayback-${res.target_url || res.url || idx}`;
  if (res.source === 'hibp')
    return `email-feed-hibp-${res.breach_name || res.details?.name || res.url || idx}`;
  if (res.ui_bucket === 'email_infrastructure')
    return `email-feed-infra-${res.details?.hostname || res.details?.email_infra_role || idx}`;
  if (String(res.platform || '').toLowerCase() === 'censys')
    return `email-feed-censys-${res.details?.email_infra_role || 'infra'}-${res.url || idx}-${idx}`;
  return `email-feed-${res.source || 'row'}-${res.platform || res.type || ''}-${res.url || idx}-${idx}`;
}

const INTERNAL_TOOLS_BLOCKLIST = new Set([
  'blackbird',
  'socialscan',
  'holehe',
  'ghunt',
  'dorking',
  'google_search',
  'googlesearch',
  'hibp',
  'pwned',
  'crawler',
  'recon',
  'profilehound',
  'serpdorking',
]);

function isInternalCollectionTool(raw) {
  const compact = String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  return Boolean(compact) && INTERNAL_TOOLS_BLOCKLIST.has(compact);
}

function dedupeServicesClient(services) {
  const byKey = new Map();
  for (const svc of services || []) {
    const nameFields = [svc.service_key, svc.service_name, svc.platform, svc.name, svc.title];
    if (nameFields.some((v) => isInternalCollectionTool(v))) {
      continue;
    }
    const key = String(svc.service_key || svc.service_name || svc.platform || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
    if (!key || isInternalCollectionTool(key)) continue;
    if (!byKey.has(key)) {
      byKey.set(key, { ...svc, sources: [...(svc.sources || [])] });
      continue;
    }
    const prev = byKey.get(key);
    const srcs = new Set([...(prev.sources || []), ...(svc.sources || [])].filter(Boolean));
    if (svc.source) srcs.add(svc.source);
    byKey.set(key, {
      ...prev,
      ...svc,
      service_name: prev.service_name || svc.service_name,
      category: prev.category || svc.category,
      account_family: prev.account_family || svc.account_family,
      manage_url: prev.manage_url || svc.manage_url,
      sources: [...srcs],
      profile_url: prev.profile_url || svc.profile_url,
      url: prev.url || svc.url,
    });
  }
  return [...byKey.values()];
}

function isGaiaWaybackRow(res) {
  if (!res || res.ui_bucket !== 'wayback_archive') return false;
  if (res.is_gaia_archive || res.gaia_id) return true;
  const hay = `${res.target_url || ''} ${res.wayback_url || ''} ${res.url || ''}`.toLowerCase();
  return hay.includes('plus.google.com');
}

function WaybackMachineSection({ rows, loading, email, deliverability }) {
  const domain = String(email || deliverability?.email || '')
    .split('@')[1]
    ?.toLowerCase();
  const publicProvider = isPublicEmailProvider(email || deliverability?.email || domain);
  // Freemail providers: domain Wayback is noise — keep only corporate / custom domains.
  if (publicProvider) return null;

  const isCorporate =
    deliverability?.type === 'corporate' || deliverability?.type_label?.includes('Corporate');
  const list = rows || [];

  if (!list.length && loading) {
    return (
      <section className="space-y-3" aria-label="Wayback Machine archives" data-testid="wayback-section-loading">
        <SectionHeader
          title="Wayback Machine"
          subtitle="Historical domain captures (Internet Archive)"
          tone="amber"
        />
        <article className={`${FEED_CARD_SHELL} animate-pulse`} aria-busy="true">
          <div className="h-14 rounded-xl bg-slate-900/80" />
        </article>
      </section>
    );
  }

  if (!list.length && isCorporate && domain) {
    const placeholder = {
      ui_bucket: 'wayback_archive',
      target_url: `https://${domain}`,
      wayback_url: `https://web.archive.org/web/*/${domain}*`,
      archive_url: `https://web.archive.org/web/*/${domain}*`,
      snapshot_count: 0,
      status: 'no_snapshots',
      title: `Wayback Archive: ${domain}`,
    };
    return (
      <section className="space-y-3" aria-label="Wayback Machine archives" data-testid="wayback-section">
        <SectionHeader
          title="Wayback Machine"
          subtitle="Historical domain captures (Internet Archive)"
          tone="amber"
        />
        <WaybackTimelineCard data={placeholder} idx="domain-placeholder" />
      </section>
    );
  }

  if (!list.length) return null;

  return (
    <section className="space-y-3" aria-label="Wayback Machine archives" data-testid="wayback-section">
      <SectionHeader
        title="Wayback Machine"
        subtitle="Historical domain captures (Internet Archive)"
        tone="amber"
        count={list.length}
      />
      {list.map((row, idx) => (
        <WaybackTimelineCard key={emailResultKey(row, idx)} data={row} idx={idx} />
      ))}
    </section>
  );
}

function GHuntFootprintCard({ data, waybackRows, onInspectMaps, onInspectCalendar }) {
  if (!data && (!waybackRows || waybackRows.length === 0)) return null;

  return (
    <section className="space-y-3" aria-label="Google identity and GHunt footprint">
      <SectionHeader
        title="Google Identity & GHunt Footprint"
        subtitle="Gaia ID · Maps · Calendar · Google+ Wayback archive"
        tone="blue"
      />
      {data ? (
        <GHuntIdentityCard
          data={data}
          onInspectMaps={onInspectMaps}
          onInspectCalendar={onInspectCalendar}
        />
      ) : null}
      {(waybackRows || []).map((row, idx) => (
        <WaybackTimelineCard
          key={emailResultKey(row, idx)}
          data={row}
          idx={idx}
        />
      ))}
    </section>
  );
}

function DeliverabilitySkeleton() {
  return (
    <article
      className={`${FEED_CARD_SHELL} animate-pulse`}
      data-testid="email-deliverability-skeleton"
      aria-busy="true"
      aria-label="Checking email deliverability"
    >
      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <span className="h-14 w-14 rounded-xl bg-slate-800/80" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-2 w-48 rounded bg-slate-800/80" />
            <div className="h-4 w-64 max-w-full rounded bg-slate-800/60" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="h-6 w-24 rounded-lg bg-slate-800/70" />
          <span className="h-6 w-28 rounded-lg bg-slate-800/70" />
          <span className="h-6 w-32 rounded-lg bg-slate-800/70" />
        </div>
        <div className="h-12 rounded-xl bg-slate-900/80" />
      </section>
    </article>
  );
}

function isConfirmedService(res) {
  const st = String(res?.status || res?.status_label || '').toLowerCase();
  return st === 'registered' || st === 'confirmed' || res?.exists === true || res?.account_exists === true;
}

function registeredServiceLabel(svc) {
  return String(svc?.name || svc?.service_name || svc?.platform || 'Service').trim();
}

function sortRegisteredServices(services) {
  return [...(services || [])].sort((a, b) =>
    registeredServiceLabel(a).localeCompare(registeredServiceLabel(b), undefined, { sensitivity: 'base' })
  );
}

function RegisteredServiceChip({ svc }) {
  const href = subscriptionHrefForService(svc);
  const name = registeredServiceLabel(svc);
  const family = String(svc.account_family || '').trim();
  const category = String(svc.category || '').trim();
  const inferred = String(svc.status_label || '').toLowerCase() === 'inferred';
  const meta = [family, category, inferred ? 'inferred' : ''].filter(Boolean).join(' · ');
  const linkTitle = svc.manage_url
    ? `Manage ${family || name}${meta ? ` (${meta})` : ''}`
    : `Open ${name}${meta ? ` (${meta})` : ''}${svc.inference_reason ? ` — ${svc.inference_reason}` : ''}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={linkTitle}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 no-underline shadow-sm transition hover:brightness-110 ${
        inferred
          ? 'border-violet-400/45 bg-violet-950/45 ring-1 ring-violet-400/20'
          : 'border-cyan-400/45 bg-cyan-950/45 ring-1 ring-cyan-400/25'
      }`}
      data-testid="registered-service-badge"
      data-account-family={family || undefined}
      data-inferred={inferred ? 'true' : undefined}
    >
      <ServiceFavicon svc={svc} name={name} />
      <span className="truncate text-[11px] font-bold leading-tight text-slate-50">{name}</span>
      {svc.manage_url ? (
        <span className="shrink-0 text-[9px] text-cyan-300" aria-hidden>
          ↗
        </span>
      ) : null}
    </a>
  );
}

function RegisteredServicesGrid({ services, probeStatuses = [], footprintScore, loading }) {
  const list = sortRegisteredServices(services);
  const probes = probeStatuses || [];
  const [expanded, setExpanded] = useState(false);
  if (!loading && list.length === 0 && probes.length === 0) return null;
  const score =
    typeof footprintScore === 'number'
      ? footprintScore
      : Math.min(100, Math.round(12 + list.length * 8.5));
  const canCollapse = list.length > REGISTERED_PLATFORMS_COLLAPSED_LIMIT;
  const visible = expanded || !canCollapse ? list : list.slice(0, REGISTERED_PLATFORMS_COLLAPSED_LIMIT);
  const hiddenCount = canCollapse && !expanded ? list.length - REGISTERED_PLATFORMS_COLLAPSED_LIMIT : 0;
  const titleCount = list.length;

  return (
    <section
      className={`${FEED_CARD_SHELL} space-y-2.5`}
      aria-label="Registered subscriptions and platforms"
      data-testid="registered-subscriptions-container"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4
          className="text-[11px] font-black uppercase tracking-[0.12em] text-cyan-200 sm:text-xs sm:tracking-[0.14em]"
          title="Confirmed platforms plus blocked / not-registered probe outcomes in one list"
        >
          Registered platforms ({titleCount})
          {probes.length > 0 ? (
            <span className="ml-1 font-semibold normal-case tracking-normal text-slate-400">
              · {probes.length} probe note{probes.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </h4>
        {loading && list.length === 0 && probes.length === 0 ? (
          <span className="rounded-md border border-cyan-500/25 bg-cyan-950/40 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-cyan-200 sm:text-[11px]">
            Scanning…
          </span>
        ) : (
          <span className="rounded-md border border-cyan-500/30 bg-cyan-950/45 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-cyan-100 sm:text-[11px]">
            Footprint {score}
          </span>
        )}
      </div>
      {list.length > 0 || probes.length > 0 ? (
        <>
          <div className="flex flex-wrap gap-1.5" data-testid="registered-platforms-list">
            {visible.map((svc, idx) => (
              <RegisteredServiceChip key={emailResultKey(svc, idx)} svc={svc} />
            ))}
            {probes.map((row, idx) => (
              <ProbeStatusChip key={`probe-${emailResultKey(row, idx)}`} row={row} />
            ))}
          </div>
          {canCollapse ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[10px] font-bold uppercase tracking-wider text-cyan-400/90 hover:text-cyan-300"
              data-testid="registered-platforms-toggle"
            >
              {expanded ? 'Show less' : `+${hiddenCount} more platforms`}
            </button>
          ) : null}
          {probes.length > 0 ? (
            <p className="text-[10px] leading-relaxed text-slate-500">
              Muted chips = probe notes (Blocked / Inconclusive / Not registered), not confirmed
              accounts.
            </p>
          ) : null}
        </>
      ) : (
        <div className="flex flex-wrap gap-1.5" aria-hidden>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-7 w-24 animate-pulse rounded-lg border border-white/5 bg-slate-900/50" />
          ))}
        </div>
      )}
    </section>
  );
}

function ProbeStatusChip({ row }) {
  const name = String(row?.service_name || row?.name || 'Service').trim();
  const state = String(row?.probe_state || row?.status_label || 'inconclusive').toLowerCase();
  const style = PROBE_STATE_STYLES[state] || PROBE_STATE_STYLES.inconclusive;
  const label = probeStateLabel(state);
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 opacity-40 ${style}`}
      title={probeStatusTitle(row)}
      data-testid="native-probe-status-badge"
      data-probe-state={state}
    >
      <ServiceFavicon svc={row} name={name} />
      <span className="truncate text-[11px] font-semibold leading-tight">{name}</span>
      <span className="shrink-0 rounded border border-current/20 px-1 py-px text-[8px] font-black uppercase tracking-wider opacity-90">
        {label}
      </span>
    </span>
  );
}

function PublicMentionsSection({ items, getDisplayName }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="space-y-3" aria-label="Public mentions and dorking leads">
      <SectionHeader
        title="Public Mentions & Dorking Leads"
        subtitle="Forum posts, classifieds, SERP dorks, domain intel & archives"
        tone="amber"
        count={items.length}
      />
      {items.map((res, idx) => {
        if (res.ui_bucket === 'cyber_trust_score' || res.ui_bucket === 'threat_intelligence') {
          return <DomainAuditCard key={emailResultKey(res, idx)} data={res} idx={idx} />;
        }

        const name = getDisplayName(res);
        if (!name) return null;

        const avatarUrl =
          res.details?.photo_url || res.details?.avatar_url || res.details?.profile_photo_url;
        const isDoc = res.type === 'document';
        const isInfra = res.type === 'infrastructure';
        return (
          <EvidenceCard
            key={emailResultKey(res, idx)}
            res={res}
            title={name}
            icon={isDoc ? '📄' : isInfra ? '📡' : '📧'}
            avatarUrl={avatarUrl}
          />
        );
      })}
    </section>
  );
}

/**
 * Email Intelligence Discovery — PROD hierarchy:
 * Deliverability → Google Footprint → HIBP → Dark Web → Wayback (corp) → Infra → Platforms → Mentions.
 *
 * @param {{
 *   displayResults: object[],
 *   loading?: boolean,
 *   getDisplayName: Function,
 *   onPivotToPerson?: Function,
 * }} props
 */
const EmailDiscovery = ({ displayResults, loading, getDisplayName, onPivotToPerson = null }) => {
  const { openInspect } = useFeedInspect();
  const [activeFilter, setActiveFilter] = useState(/** @type {EmailFeedFilterId} */ ('all'));

  const {
    deliverability,
    hibpStatus,
    hibpBreaches,
    ghuntFootprint,
    gaiaWaybacks,
    domainWaybacks,
    registeredServices,
    nativeProbeStatuses,
    footprintScore,
    mentions,
    infraRows,
    darkwebAdvisory,
    scanEmail,
  } = useMemo(() => {
    const deliv = [];
    const hibp = [];
    let hibpSummary = null;
    const ghunt = [];
    const gaiaWb = [];
    const domainWb = [];
    const services = [];
    const infra = [];
    const feed = [];
    let darkweb = null;
    let fp = 0;
    let mailbox = '';
    (displayResults || []).forEach((r) => {
      if (!r || r.type === 'log' || r.type === 'cache_meta') return;
      if (r.ui_bucket === 'email_deliverability') {
        deliv.push(r);
        mailbox = r.email || mailbox;
        return;
      }
      if (r.ui_bucket === 'darkweb_advisory') {
        darkweb = r;
        return;
      }
      if (r.ui_bucket === 'ghunt_footprint') {
        ghunt.push(r);
        return;
      }
      if (r.ui_bucket === 'hibp_status' || r.source === 'hibp_status') {
        hibpSummary = r;
        return;
      }
      if (r.ui_bucket === 'registered_service' && isConfirmedService(r)) {
        services.push(r);
        if (typeof r.footprint_score === 'number') fp = Math.max(fp, r.footprint_score);
        return;
      }
      if (r.ui_bucket === 'registered_service') return;
      if (r.ui_bucket === 'native_probe_status') return;
      if (PLATFORM_FEED_SOURCES.has(String(r.source || '').toLowerCase())) return;
      if (r.source === 'ghunt') return;
      if (r.source === 'hibp') {
        hibp.push(r);
        return;
      }
      if (r.ui_bucket === 'wayback_archive') {
        if (isGaiaWaybackRow(r)) gaiaWb.push(r);
        else domainWb.push(r);
        return;
      }
      if (r.ui_bucket === 'email_infrastructure' || String(r.source || '') === 'email_infra_probe') {
        infra.push(r);
        return;
      }
      if (String(r.platform || '').toLowerCase() === 'censys') return;
      feed.push(r);
    });
    const deduped = dedupeServicesClient(services);
    const probeStatuses = filterProbeStatusesAgainstRegistered(
      collectNativeProbeStatuses(displayResults),
      deduped
    );
    if (!fp) fp = Math.min(100, Math.round(12 + deduped.length * 8.5));
    return {
      deliverability: deliv[0] || null,
      hibpStatus: hibpSummary,
      hibpBreaches: hibp,
      ghuntFootprint: ghunt[0] || null,
      gaiaWaybacks: gaiaWb,
      domainWaybacks: domainWb,
      registeredServices: deduped,
      nativeProbeStatuses: probeStatuses,
      footprintScore: fp,
      mentions: feed,
      infraRows: mergeEmailServerRoutingRows(dedupeEmailInfrastructureRows(infra)),
      darkwebAdvisory: darkweb,
      scanEmail: mailbox,
    };
  }, [displayResults]);

  const mailboxForPivot = scanEmail || deliverability?.email || '';
  const personPivot = useMemo(
    () => buildEmailPersonPivotContext(mailboxForPivot, displayResults || []),
    [mailboxForPivot, displayResults],
  );

  const mentionCards = useMemo(
    () => (mentions || []).filter((r) => Boolean(getDisplayName?.(r))),
    [mentions, getDisplayName],
  );

  const identityCount =
    (deliverability || loading ? 1 : 0) +
    (ghuntFootprint ? 1 : 0) +
    (gaiaWaybacks || []).length;
  const breachesRiskCount =
    (hibpStatus || (hibpBreaches || []).length > 0 || loading ? 1 : 0) +
    (darkwebAdvisory || (loading && !darkwebAdvisory) ? 1 : 0);
  const infrastructureCount = (domainWaybacks || []).length + (infraRows || []).length;
  const platformsCount =
    registeredServices.length > 0 || nativeProbeStatuses.length > 0 || loading ? 1 : 0;
  const mentionsCount = mentionCards.length;

  const filterCounts = useMemo(
    () => ({
      all:
        identityCount +
        breachesRiskCount +
        infrastructureCount +
        platformsCount +
        mentionsCount,
      identity: identityCount,
      breaches_risk: breachesRiskCount,
      infrastructure: infrastructureCount,
      platforms: platformsCount,
      mentions: mentionsCount,
    }),
    [
      identityCount,
      breachesRiskCount,
      infrastructureCount,
      platformsCount,
      mentionsCount,
    ],
  );

  if (!displayResults?.length && !loading) return null;

  return (
    <div className="touch-manipulation min-w-0 max-w-full space-y-2.5 overflow-x-clip text-sm" data-testid="email-discovery-hierarchy">
      <CategoryTabs
        filters={EMAIL_FEED_FILTERS}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        counts={filterCounts}
        ariaLabel="Email result categories"
        testId="email-feed-filters"
        dangerFilterId="breaches_risk"
        hideZeroCounts
        mobileEssentialIds={['all', 'identity', 'breaches_risk']}
      />

      {showEmailSection(activeFilter, 'identity') ? (
        <>
          {deliverability ? (
            <DeliverabilityCard
              data={deliverability}
              personPivot={personPivot}
              onPivotToPerson={onPivotToPerson}
            />
          ) : loading ? (
            <DeliverabilitySkeleton />
          ) : null}

          <GHuntFootprintCard
            data={ghuntFootprint}
            waybackRows={gaiaWaybacks}
            onInspectMaps={(row) => openInspect(row, { variant: 'ghunt', ghuntMode: 'maps' })}
            onInspectCalendar={(row) => openInspect(row, { variant: 'ghunt', ghuntMode: 'calendar' })}
          />
        </>
      ) : null}

      {showEmailSection(activeFilter, 'breaches_risk') ? (
        <>
          <HibpCard
            email={scanEmail || deliverability?.email}
            status={hibpStatus}
            breaches={hibpBreaches}
            loading={loading}
            onInspect={(payload) =>
              openInspect(
                { ...payload, source: 'hibp', platform: 'Have I Been Pwned' },
                {
                  variant: 'hibp',
                  email: payload?.email,
                  status: payload?.status,
                  breaches: hibpBreaches,
                },
              )
            }
          />

          <DarkWebAdvisoryCard data={darkwebAdvisory} loading={loading && !darkwebAdvisory} />
        </>
      ) : null}

      {showEmailSection(activeFilter, 'infrastructure') ? (
        <>
          <WaybackMachineSection
            rows={domainWaybacks}
            loading={loading}
            email={scanEmail || deliverability?.email}
            deliverability={deliverability}
          />

          <EmailInfrastructureSection
            rows={infraRows}
            loading={loading}
            onInspect={(row) => openInspect(row)}
          />
        </>
      ) : null}

      {showEmailSection(activeFilter, 'platforms') ? (
        <RegisteredServicesGrid
          services={registeredServices}
          probeStatuses={nativeProbeStatuses}
          footprintScore={footprintScore}
          loading={loading}
        />
      ) : null}

      {showEmailSection(activeFilter, 'mentions') ? (
        <PublicMentionsSection items={mentionCards} getDisplayName={getDisplayName} />
      ) : null}
    </div>
  );
};

export default EmailDiscovery;
